/**
 * Particle materials and effect recipes (style guide §3.6 weapon and impact colours). Asset-lane owned (task A11).
 * Gameplay particle pools render these on the quad + instanced-attribute geometry described by PARTICLE_ATTR
 * (src/render/contracts.ts). P0 STUB: procedural radial billboards and streaks, no flipbook.
 */
import { AdditiveBlending, Color, MeshBasicNodeMaterial, NormalBlending, type Material } from 'three/webgpu'
import {
  attribute,
  cameraProjectionMatrix,
  cameraViewMatrix,
  color,
  float,
  max,
  mix,
  positionGeometry,
  select,
  uv,
  vec2,
  vec4,
} from 'three/tsl'
import { z } from 'zod'
import {
  PARTICLE_ATTR,
  VFX_EFFECT_IDS,
  VFX_MATERIAL_KEYS,
  type VfxEffectId,
  type VfxEmitterSpec,
  type VfxMaterialKey,
  type VfxRecipeBook,
} from '../contracts'
import type { MaterialLibrary } from './library'
import vfxJson from './vfx.json'

export interface VfxMaterials extends Readonly<Record<VfxMaterialKey, Material>> {
  readonly recipes: VfxRecipeBook
}

const hex = z.string().regex(/^#[0-9A-Fa-f]{6}$/)
const range = z.tuple([z.number().nonnegative(), z.number().nonnegative()]).refine((r) => r[0] <= r[1], 'min must be <= max')
const materialKey = z.enum(VFX_MATERIAL_KEYS as unknown as [VfxMaterialKey, ...VfxMaterialKey[]])
const emitterSchema = z.object({
  material: materialKey,
  count: z.number().positive(),
  life: range,
  size: range,
  stretch: range,
  speed: range,
  coneDeg: z.number().min(0).max(180),
  inheritVelocity: z.number().min(0).max(1),
  drag: z.number().nonnegative(),
  tint: z.tuple([z.number().nonnegative(), z.number().nonnegative(), z.number().nonnegative()]),
})
const colorSpec = z.object({ core: hex, halo: hex, intensity: z.number().positive(), additive: z.boolean() })

export const vfxFileSchema = z.object({
  colors: z.record(materialKey, colorSpec),
  recipes: z.record(z.enum(VFX_EFFECT_IDS as unknown as [VfxEffectId, ...VfxEffectId[]]), z.array(emitterSchema).min(1)),
})

export function loadVfxData(): z.infer<typeof vfxFileSchema> {
  const data = vfxFileSchema.parse(vfxJson)
  for (const key of VFX_MATERIAL_KEYS) if (!data.colors[key]) throw new Error(`vfx.json: colour for ${key} missing`)
  for (const id of VFX_EFFECT_IDS) if (!data.recipes[id]) throw new Error(`vfx.json: recipe ${id} missing`)
  return data
}

/** Billboard (aSize.y = 0) or streak along aDir (aSize.y = length), built in view space. */
function particleMaterial(core: string, halo: string, intensity: number, additive: boolean): Material {
  const aPos = attribute(PARTICLE_ATTR.pos, 'vec3')
  const aAge = attribute(PARTICLE_ATTR.age, 'float')
  const aTint = attribute(PARTICLE_ATTR.tint, 'vec3')
  const aSize = attribute(PARTICLE_ATTR.size, 'vec2')
  const aDir = attribute(PARTICLE_ATTR.dir, 'vec3')
  const quad = positionGeometry.xy
  const viewPos = cameraViewMatrix.mul(vec4(aPos, 1))
  const dirView = cameraViewMatrix.mul(vec4(aDir, 0)).xy
  const len = dirView.length()
  const axis = select(len.greaterThan(1e-4), dirView.div(max(len, 1e-4)), vec2(1, 0))
  const perp = vec2(axis.y.negate(), axis.x)
  const along = max(aSize.x, aSize.y)
  const offset = axis.mul(quad.x.mul(along)).add(perp.mul(quad.y.mul(aSize.x)))

  const m = new MeshBasicNodeMaterial({
    transparent: true,
    depthWrite: false,
    blending: additive ? AdditiveBlending : NormalBlending,
  })
  m.vertexNode = cameraProjectionMatrix.mul(vec4(viewPos.xy.add(offset), viewPos.z, 1))
  const d = uv().sub(0.5).length().mul(2).oneMinus().max(0)
  const life = float(1).sub(aAge)
  m.colorNode = mix(color(new Color(halo)), color(new Color(core)), d.mul(d).mul(d)).mul(intensity).mul(aTint)
  m.opacityNode = d.mul(d).mul(life)
  return m
}

export function createVfxMaterials(lib: MaterialLibrary): VfxMaterials {
  void lib
  const data = loadVfxData()
  const out: Partial<Record<VfxMaterialKey, Material>> = {}
  for (const key of VFX_MATERIAL_KEYS) {
    const c = data.colors[key]
    if (!c) throw new Error(`vfx colour ${key} missing`)
    const m = particleMaterial(c.core, c.halo, c.intensity, c.additive)
    m.name = `vfx:${key}`
    out[key] = m
  }
  const recipes = data.recipes as Record<VfxEffectId, VfxEmitterSpec[]>
  return { ...(out as Record<VfxMaterialKey, Material>), recipes }
}
